<template>
  <div class="ecosystem-member-spotting-areas has-action-bar">
    <div v-if="fetchingContent" style="display: flex; justify-content: center;">
      <icon name="spinner"/>
    </div>
    <div v-else>
      <div class="pull-right side-panel__spotting-area-header-button-container" style="text-align: right;">
        <div class="spotting-area-list-container">
          <dropdown
            new-prepend-icon="microscope"
            new-append-icon="chevron-down"
            icon-fill="black"
            v-model="selectedDropdownOption"
            :options="availableSpottingAreas"
            style="width: 100%; border: 1px solid var(--primary)"
            v-if="availableSpottingAreas.length > 0"
          />
        </div>
        <DsButton :label="`Create new ${spottingAreaLabel}`" icon="plus" @click="displayCreateSpottingAreasModal()"/>
        <DsButton
          icon="trash" @click="destroy(selectedSpottingArea)"
          v-if="isMember && selectedSpottingArea && selectedSpottingArea.id > 0"
        />
      </div>

      <div v-if="spottingAreas.length < 1">
        <span>Before you can start managing {{ spottingAreasLabel }}, please create one.</span>
      </div>

      <div v-else>
        <div v-if="selectedSpottingArea.id !== -1">
          <h3 class="h3">{{ spottingAreaLabel }} Information</h3>
          <div class="ecosystem-member-communities__general-container" style="width: 40%">
            <div class="ecosystem-member-communities__general-info">
              <div>
                <form-group :label="`${spottingAreaLabel} Name`" class="ecosystem-member-communities__general-input">
                  <ds-input required v-model="selectedSpottingArea.name"/>
                  <div v-if="errors" class="form-group__error">
                    <div v-if="errors.name">
                      <span>{{ errors.name[0] }}</span>
                    </div>
                  </div>
                </form-group>

                <form-group
                  :label="`${spottingAreaLabel} Description ${isRequired ? '*' : ''}`" class="ecosystem-member-communities__general-input"
                  info-text="This text will be used in the navigation header"
                >
                  <!--                  <ds-textarea :placeholder="`Provide a description of your ${spottingAreaLabel}`" required
                                        v-model="selectedSpottingArea.description"
                                        class="spotting-area__description"/>-->

                  <rich-text-area
                    :custom-placeholder="`Provide a description of your ${spottingAreaLabel}`" required
                    v-model="selectedSpottingArea.description"
                    class="spotting-area__description"
                  />
                  <div v-if="errors" class="form-group__error">
                    <div v-if="errors.description">
                      <span>{{ errors.description[0] }}</span>
                    </div>
                  </div>
                </form-group>

                <form-group
                  :label="`${spottingAreaLabel} About Text`" class="ecosystem-member-communities__general-input"
                  info-text="This text will be displayed as a separate tab in the spotting area detail page"
                >
                  <rich-text-area
                    custom-placeholder=""
                    v-model="selectedSpottingArea.about_text"
                    class="spotting-area__description"
                  />
                  <div v-if="errors" class="form-group__error">
                    <div v-if="errors.description">
                      <span>{{ errors.description[0] }}</span>
                    </div>
                  </div>
                </form-group>

                <form-group
                  :label="`Add the actor portfolio's that belong to this ${spottingAreaLabel}`"
                  class="ecosystem-member-communities__general-input"
                >
                  <dropdown
                    v-model="selectedSpottingArea.portfolios" :multiple="true" search :options="portfolios"
                    :allowClear="true"
                  />

                  <div v-if="errors" class="form-group__error">
                    <div v-if="errors.actor_portfolios">
                      <span>{{ errors.actor_portfolios[0] }}</span>
                    </div>
                  </div>

                  <template v-slot:instructions>
                    <a
                      v-if="!saving"
                      href="#"
                      @click.stop="handleViewPortfolios"
                    >
                      View all portfolio's
                    </a>
                  </template>
                </form-group>

                <form-group
                  :label="`Manage the trend monitors that belong to this ${spottingAreaLabel}`"
                  class="ecosystem-member-communities__general-input"
                >
                  <dropdown
                    v-model="selectedSpottingArea.trend_monitors" :multiple="true" search
                    :options="conceptSearches" :allowClear="true"
                  />

                  <div v-if="errors" class="form-group__error">
                    <div v-if="errors.trend_monitors">
                      <span>{{ errors.trend_monitors[0] }}</span>
                    </div>
                  </div>

                  <template v-slot:instructions>
                    <a
                      v-if="!saving"
                      href="#"
                      @click.stop="handleViewTrendMonitors"
                    >
                      View trend monitors
                    </a>
                  </template>
                </form-group>

                <!-- Manage lexicons (dictionaries) -->
                <form-group :label="`Manage the lexicons that belong to this ${spottingAreaLabel} (max 1.)`">
                  <dropdown
                    :multiple="false"
                    :search="true"
                    :allow-clear="true"
                    :options="topicOptions"
                    v-model="selectedSpottingArea.context"
                    :disabled="saving"
                    :class="{ 'disabled-while-editing': saving }"
                  />

                  <div v-if="errors" class="form-group__error">
                    <div v-if="errors.context">
                      <span>{{ errors.context[0] }}</span>
                    </div>
                  </div>

                  <template v-slot:instructions>
                    <a
                      v-if="!saving"
                      href="#"
                      @click.stop="handleEditTopics"
                    >
                      View lexicons
                    </a>
                  </template>
                </form-group>

                <!-- Preferred City -->
                <form-group :label="`Preferred city/region for this ${spottingAreaLabel}. Note: Only cities/regions from the actors in this spotting area are available`"
                            info-text="Select a city/region of which the subsidiaries will be visible on the map, all other subsidiaries from other cities will be hidden.">
                  <dropdown
                    :search="true"
                    :allow-clear="true"
                    :options="citySearchOptions"
                    :loading="citySearchLoading"
                    v-model="selectedSpottingArea.preferred_city_name"
                    @update:modelValue="handleCitySelection"
                    :disabled="saving || citySearchLoading"
                    placeholder="Search for a city..."
                  />

                  <div v-if="errors" class="form-group__error">
                    <div v-if="errors.preferred_city_name">
                      <span>{{ errors.preferred_city_name[0] }}</span>
                    </div>
                  </div>
                </form-group>

              </div>

              <form-group label="Configure access">
                <label v-if="$store.getters.isSpottingAreaScopeEnabled" class="form-group__header">
                  <div class="form-group__label__text">
                    <Icon name="information-outline" size="18" style="vertical-align: sub;"/>
                    Don't forget to configure the accessible spotting areas per role in the section 'Accessible data' on
                    <router-link to="/settings/publisher">
                      the permissions
                    </router-link>
                  </div>
                </label>
                <label
                  v-if="selectedSpottingArea.highlight && (oldSpottingAreaType !== selectedSpottingArea.type) && selectedSpottingArea.type !== SPOTTING_AREA_TYPES.PUBLIC"
                >
                  <div
                    class="form-group__label__text"
                    style="color: red"
                  >
                    <Icon name="information-outline" size="18" style="vertical-align: sub;"/>
                    WARNING: Saving these changes will remove the <strong>highlighted</strong> status of this spotting
                    area.
                  </div>
                </label>
                <radio-button
                  :model-value="selectedSpottingArea.type"
                  @update:modelValue="selectedSpottingArea.type = $event"
                  name="type"
                  label="Public (for visitors and ecosystem members: always visible & only available through permission-based access, portfolio members can explore by default)"
                  val="public"
                />

                <radio-button
                  :model-value="selectedSpottingArea.type"
                  @update:modelValue="selectedSpottingArea.type = $event"
                  name="type"
                  label="Premium (for portfolio members: invite only, not available for visitors and ecosystem members)"
                  val="premium"
                />

                <radio-button
                  :model-value="selectedSpottingArea.type"
                  @update:modelValue="selectedSpottingArea.type = $event"
                  name="type"
                  label="Private (only for owners and team members)"
                  val="private"
                />
              </form-group>

              <div v-if="selectedSpottingArea.type === SPOTTING_AREA_TYPES.PUBLIC" class="spotting-areas-highlighted">
                <form-group
                  :label="`Which users can contribute on this spotting area? (only portfolio members)`"
                  class="ecosystem-member-communities__general-input"
                >
                  <dropdown
                    v-model="selectedSpottingArea.contributors" :multiple="true" search
                    :options="allPortfolioMembers"
                    :allowClear="true"
                  />

                  <div v-if="errors" class="form-group__error">
                    <div v-if="errors.contributors">
                      <span>{{ errors.contributors[0] }}</span>
                    </div>
                  </div>
                </form-group>

                <form-group
                  :label="`Highlight this ${spottingAreaLabel} & choose the highlighted color`"
                  info-text="Make sure the spotting area is added to the accessible data, configurable in the permissions per role."
                >
                  <div
                    class="spotting-area-highlight-container"
                    style="display: flex; flex-direction: row;"
                    v-if="availableSpottingAreas.length > 0"
                  >

                    <div style="display: flex; flex-direction: row;align-items: center;">
                      <input
                        type="checkbox"
                        v-model="selectedSpottingArea.highlight"
                        :value="selectedSpottingArea.highlight"
                      />
                      <h5>Highlight this spotting area</h5>
                    </div>
                    <color-picker
                      :colors="{}"
                      title=""
                      v-model="selectedSpottingArea.highlight_color"
                      @updatePrimary="selectedSpottingArea.highlight_color = $event"
                      :initial-value="selectedSpottingArea.highlight_color"
                    />
                  </div>
                </form-group>
              </div>

              <div v-if="selectedSpottingArea.type === SPOTTING_AREA_TYPES.PREMIUM">
                <form-group
                  :label="`Which users can explore this spotting area? (only portfolio members)`"
                  class="ecosystem-member-communities__general-input"
                >
                  <dropdown
                    v-model="selectedSpottingArea.explorers" :multiple="true" search
                    :options="allPortfolioMembers"
                    :allowClear="true"
                  />

                  <div v-if="errors" class="form-group__error">
                    <div v-if="errors.explorers">
                      <span>{{ errors.explorers[0] }}</span>
                    </div>
                  </div>
                </form-group>

                <form-group
                  :label="`Which users can contribute on this spotting area? (only portfolio members)`"
                  class="ecosystem-member-communities__general-input"
                >
                  <dropdown
                    v-model="selectedSpottingArea.contributors" :multiple="true" search
                    :options="allPortfolioMembers"
                    :allowClear="true"
                  />

                  <div v-if="errors" class="form-group__error">
                    <div v-if="errors.contributors">
                      <span>{{ errors.contributors[0] }}</span>
                    </div>
                  </div>
                </form-group>
              </div>


              <form-group :label="`${spottingAreaLabel} Picture`" class="ecosystem-member-communities__general-input">
                <image-input
                  :allow-remove="true"
                  @remove="selectedSpottingArea.image = ''"
                  v-model="selectedSpottingArea.image"
                  @input="selectedSpottingArea.image_name = $event"
                  width="512"
                  height="288"
                  :placeholder="$t('community_image_placeholder')"
                  :is-simplified="true"
                />
              </form-group>

              <!-- Spotting Area Features Settings -->
              <form-group :label="`${spottingAreaLabel} Features`" class="ecosystem-member-communities__general-input">
                <div style="margin-top: 10px;">
                  <checkbox
                    v-model="selectedSpottingArea.enable_products"
                    :label="`Enable Products - Show product tab in this ${spottingAreaLabel}`"
                    style="margin-bottom: 10px;"
                  />
                  <checkbox
                    v-model="selectedSpottingArea.enable_news"
                    :label="`Enable News - Show news tab in this ${spottingAreaLabel}`"
                  />
                  <checkbox
                    v-model="selectedSpottingArea.enable_knowledge_base"
                    :label="`Enable ${knowledgeBaseLabel} - Show ${knowledgeBaseLabel} tab in this ${spottingAreaLabel}`"
                  />
                </div>
              </form-group>

              <!-- Map Configuration -->
              <form-group
                :label="`${spottingAreaLabel} Map Settings`" class="ecosystem-member-communities__general-input"
                info-text="Configure custom map center and zoom for this spotting area. If not set, the ecosystem default will be used."
              >
                <div class="row">
                  <div class="col-xs-4">
                    <ds-input v-model="selectedSpottingArea.map_latitude" placeholder="Latitude" type="number" step="any"/>
                  </div>
                  <div class="col-xs-4">
                    <ds-input v-model="selectedSpottingArea.map_longitude" placeholder="Longitude" type="number" step="any"/>
                  </div>
                  <div class="col-xs-4">
                    <ds-input v-model="selectedSpottingArea.map_zoom" placeholder="Zoom (1-18)" type="number" min="1" max="18"/>
                  </div>
                </div>
                <div style="margin-top: 15px;">
                  <h5>Click on the map to set coordinates:</h5>
                  <div class="spotting-area-map-config relative overflow-hidden" style="width: 60vw; height: 600px; border: 1px solid #ddd; border-radius: 4px;">
                    <mapView
                      ref="mapPane"
                      :data="[]"
                      :getMapData="getMapData"
                      :getData="getMapData"
                      @selectedLat="updateMapCoordinates"
                      :enable-on-click="true"
                      :noSearchWhileMoving="true"
                      :disableExpandButton="true"
                      :disable-mouse-wheel-zoom="true"
                      :customCenter="mapCenter"
                      :customZoom="selectedSpottingArea.map_zoom"
                    />
                  </div>
                </div>
              </form-group>
            </div>
          </div>

          <action-bar :editing="true">
            <ds-button
              variant="primary" :label="`Save ${spottingAreaLabel}`" @click="updateSpottingArea()"
              :disabled="saving" :loading="saving"
            />
            <span class="action-bar__message" v-if="errors && errors.message">{{ errors.message }}</span>
            <span
              class="action-bar__message"
              v-else-if="saved && !saving"
            >{{ spottingAreaLabel }} has been updated</span>
          </action-bar>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { deleteSpottingArea, ManageSpottingAreas, searchGeoCity } from '../../api/spottingareas'
  import { Dictionary, fetchAllConceptSearches } from '../../api/exploration'
  import { fetchAllPortfolios } from '../../api/portfolios'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui'
  import MODAL_IDS from '../../constants/modal-ids'
  import Dropdown from '../Dropdown/Dropdown.vue'
  import ActionBar from '../Form/ActionBar.vue'
  import ImageInput from '../Form/ImageInput.vue'
  import AutocompleteDropdown from '../Dropdown/AutocompleteDropdown.vue'
  import DsTextarea from '../../components/Form/DsTextarea.vue'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import RadioButton from '../Form/RadioButton.vue'
  import { fetchUsers } from '../../api/config'
  import { defineComponent } from 'vue'
  import { ACTION_TYPES as SPOTTING_AREAS_ACTION_TYPES } from '@/store/modules/spottingareas'
  import { SPOTTING_AREA_TYPES } from '@/store/modules/config.js'
  import RichTextArea from '@/components/TextArea/RichTextArea.vue'
  import ColorPicker from '@/components/Form/ColorPicker.vue'
  import FormGroup from '@/components/Form/FormGroup.vue'
  import MapView from '@/components/MapView/MapView.vue'
  import { ACTION_TYPES as ACTORS_ACTION_TYPES } from '@/store/modules/actors'
  import Checkbox from '@/components/Form/Checkbox.vue'
  import UserMixin from '@/util/UserMixin'

  export default defineComponent({
    name: 'SpottingAreas.vue',
    data () {
      return {
        fetchingContent: false,
        spottingAreas: [],
        portfolios: [],
        conceptSearches: [],
        oldSpottingAreaType: null,
        selectedSpottingArea: {
          id: -1,
          name: null,
          description: '',
          about_text: '',
          type: SPOTTING_AREA_TYPES.PREMIUM,
          managers: [],
          members: [],
          permissions: {},
          image: null,
          image_path: null,
          file: null,
          ecosystem_id: 0,
          actor_portfolios: [],
          trend_monitors: [],
          context: [],
          explorers: [],
          contributors: [],
          highlight: false,
          highlight_color: '#000000',
          map_latitude: null,
          map_longitude: null,
          map_zoom: 8,
          preferred_city_geo_id: null,
          preferred_city_name: null,
        },
        selectedUsers: [],
        dictionaries: null,
        saving: false,
        errors: {},
        saved: false,
        minTitleLength: 2,
        minBodyLength: 10,
        allPortfolioMembers: [],
        originalPortfolioMembers: [],
        mapComponentReady: false,
        citySearchOptions: [],
        citySearchLoading: false,
      }
    },
    computed: {
      mapData () {
        return this.$store.state.actors.mapData
      },
      renderMap () {
        this.$refs.mapPane && this.$refs.mapPane[0] && this.$refs.mapPane[0].invalidateSize()
      },
      SPOTTING_AREA_TYPES () {
        return SPOTTING_AREA_TYPES
      },
      isRequired () {
        return this.selectedSpottingArea.type !== SPOTTING_AREA_TYPES.PREMIUM
      },
      isOwner () {
        return this.$store.getters.isOwner
      },
      isMember () {
        return this.$store.getters.isMember
      },
      isSelectedSpottingAreaNew () {
        return this.selectedSpottingArea && !this.selectedSpottingArea.id
      },
      availableSpottingAreas () {
        if (!this.spottingAreas || this.spottingAreas.length === 0) {
          return []
        }

        return this.spottingAreas.map(SpottingArea => { // package is a reserved JS word
          return {
            value: SpottingArea.id,
            label: SpottingArea.name,
          }
        })
      },
      topicOptions () {
        if (!this.dictionaries) {
          return []
        }

        return this.dictionaries.map(item => ({
          value: item.id,
          label: item.value,
        }))
      },
      selectedDropdownOption: {
        get () {
          if (!this.selectedSpottingArea || !this.selectedSpottingArea.id) {
            return null
          }

          return {
            value: this.selectedSpottingArea.id,
            label: this.selectedSpottingArea.name,
          }
        },
        set (spottingAreaId) {
          const spottingArea = this.spottingAreas.find(p => p.id === spottingAreaId)
          if (!spottingArea) return

          this.selectedSpottingArea = spottingArea
          this.oldSpottingAreaType = spottingArea.type

          if (this.selectedSpottingArea.image) {
            this.selectedSpottingArea.image = window.location.protocol + '//' + window.location.hostname + '/api/spotting-areas/' + this.selectedSpottingArea.id + '/image'
          }

          this.fetchSpottingAreaCities()

          if (this.selectedSpottingArea.description) {
            this.selectedSpottingArea.description = this.selectedSpottingArea.description.replace('<p>', '').replace('</p>', '')
          }

          // API already returns snake_case fields (map_latitude, map_longitude, map_zoom)
          // No field mapping needed

          this.errors = {}

          // Refresh map after switching spotting area
          // Force map refresh by triggering reactivity
          this.$nextTick(() => {
            this.refreshMap()
          })
        },
      },
      mapCenter () {
        if (!this.selectedSpottingArea?.map_latitude || !this.selectedSpottingArea?.map_longitude) {
          return null
        }
        const center = [
          parseFloat(this.selectedSpottingArea.map_latitude),
          parseFloat(this.selectedSpottingArea.map_longitude)
        ]
        return center
      },
    },
    methods: {
      getMapData () {
        let filters
        if (this.$store.state.config && this.$store.state.config.viewActorTypes && this.$store.state.config.viewActorTypes.length) {
          filters = Object.assign({}, this.$store.getters.mapFilterObject, { actor_type: this.$store.state.config.viewActorTypes })
        } else {
          filters = Object.assign({}, this.$store.getters.mapFilterObject)
        }

        if (filters.tl) {
          this.$store.dispatch(ACTORS_ACTION_TYPES.FETCH_ACTORS_MAP, filters)
        }
      },
      fetchPortfolioMembers () {
        fetchUsers({ role: 'portfolio_member', limit: 50 }).then((response) => {
          const portfolioMembers = response.data.filter(user => user.verified && user.name.toLowerCase() !== 'removed user')

          portfolioMembers.forEach((member) => {
            const memberObject = {
              label: member.name + ' - ' + member.email,
              value: member.id,
            }
            this.allPortfolioMembers.push(memberObject)
            this.originalPortfolioMembers.push(memberObject)
          })
        })
      },
      fetchPortfolios () {
        fetchAllPortfolios({ limit: 1000, type: 'all' })
          .then(portfolios => {
            var portfolioOptions = []

            portfolios.forEach((portfolio) => {
              if (portfolio.virtual) {
                return
              }

              // Check if portfolio starts with Default Portfolio -
              if (portfolio.name.startsWith('Portfolio for spotting area ')) {
                return
              }

              portfolioOptions.push({ value: portfolio.id, label: portfolio.name })
            })

            this.portfolios = portfolioOptions
          })
          .catch(errors => {
            console.log(errors)
          })
      },
      fetchTrendMonitors () {
        fetchAllConceptSearches({})
          .then(conceptSearches => {
            var conceptSearchOptions = []

            conceptSearches.forEach((conceptSearch) => {
              conceptSearchOptions.push({ value: conceptSearch.id, label: conceptSearch.title })
            })

            this.conceptSearches = conceptSearchOptions
          })
      },
      displayCreateSpottingAreasModal () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, null)
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CREATE_SPOTTING_AREA)
      },
      removeDuplicates (suggestions) {
        return suggestions
      },
      confirmDeleteSpottingArea (spottingAreaId) {
        deleteSpottingArea({ spottingAreaId: spottingAreaId })
          .then(response => {
            this.fetchSpottingAreas(true)

            // Refresh the spotting areas list (crf. spotting areas overview page)
            this.$store.dispatch(SPOTTING_AREAS_ACTION_TYPES.FETCH_SPOTTING_AREAS_LIST)
          })
          .catch(error => {
            console.log(error)
          })
      },
      updateSpottingArea () {
        this.saving = true
        this.saved = false
        this.errors = {}

        var spottingAreaId = this.selectedSpottingArea.id

        if (this.selectedSpottingArea.highlight
          && this.selectedSpottingArea.type !== SPOTTING_AREA_TYPES.PUBLIC) {
          this.selectedSpottingArea.highlight = false
        }

        ManageSpottingAreas
          .post(this.selectedSpottingArea)
          .then(response => {
            if (response.success) {
              this.$bus.emit('spottingAreaUpdated', { spottingAreaId: spottingAreaId })

              this.saved = true
              this.saving = false
            }

            this.$store.dispatch(SPOTTING_AREAS_ACTION_TYPES.FETCH_SPOTTING_AREA_HIGHLIGHT)
          })
          .catch(errors => {
            this.errors = errors
            this.saving = false
          })
      },
      fetchDictionaryOptions () {
        // we should reconsider fetching the data when clicking the dropdown, so that the data is up to date when selecting an option
        Dictionary
          .get()
          .then(dictionaries => {
            this.dictionaries = dictionaries
          })
      },
      handleViewPortfolios () {
        window.open(window.location.protocol + '//' + window.location.host + '/portfolios?includeDefaultPortfolios=true', '_blank')
      },
      handleViewTrendMonitors () {
        window.open(window.location.protocol + '//' + window.location.host + '/dashboards/explore-content', '_blank')
      },
      handleEditTopics () {
        window.open(window.location.protocol + '//' + window.location.host + '/settings/exploration', '_blank')
      },
      fetchSpottingAreaCities () {
        this.citySearchLoading = true

        try {
          searchGeoCity(this.selectedSpottingArea.id)
            .then((response) => {
              this.citySearchOptions = response
            })
        } catch (error) {
          console.error('Error searching cities:', error)
          this.citySearchOptions = []
        } finally {
          this.citySearchLoading = false
        }
      },
      handleCitySelection (cityName) {
        // The dropdown emits the value (city name) when an option is selected
        this.selectedSpottingArea.preferred_city_name = cityName
      },
      destroy (SpottingArea) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          name: SpottingArea.title,
          resource: SpottingArea,
          modalContextType: 'spotting-area',
        })

        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      updateMapCoordinates (coordinateData) {
        this.selectedSpottingArea.map_latitude = coordinateData.latLng.lat
        this.selectedSpottingArea.map_longitude = coordinateData.latLng.lng
        this.selectedSpottingArea.map_zoom = coordinateData.zoom
      },
      refreshMap () {
        // Refresh the map when switching between spotting areas
        console.log('Refreshing map with center:', this.mapCenter)
        this.$nextTick(() => {
          if (this.$refs.mapPane) {
            // Invalidate size first
            if (this.$refs.mapPane.invalidateSize) {
              this.$refs.mapPane.invalidateSize()
            }
            // Then refresh the map with new coordinates
            setTimeout(() => {
              if (this.$refs.mapPane.refreshMap) {
                this.$refs.mapPane.refreshMap()
              }
            }, 100)
          }
        })
      },
      fetchSpottingAreas (selectFirst = false, last = false) {
        this.fetchingContent = true

        ManageSpottingAreas
          .get()
          .then(response => {
            this.spottingAreas = response.map(c => {
              const spottingArea = { ...c }

              spottingArea.portfolios = spottingArea.portfolios.filter((portfolio) => {
                return this.isDeveloper ? true : !portfolio.label.startsWith('Default Portfolio -')
              })

              return spottingArea
            })


            // Select the appropriate spotting area on initial load
            if (this.spottingAreas.length > 0) {
              let selectedArea = null

              if (last && !selectFirst) {
                this.spottingAreas.sort((a, b) => b.id - a.id)
                selectedArea = this.spottingAreas[0]
              } else if (!last && selectFirst) {
                selectedArea = this.spottingAreas[0]
              }

              if (selectedArea) {
                this.selectedDropdownOption = selectedArea.id // This will trigger the setter
              }
            }

            this.fetchingContent = false
          })
          .catch(errors => {
            console.log(errors)
          })
      },
    },
    components: {
      Checkbox,
      FormGroup, ColorPicker,
      RichTextArea,
      RadioButton,
      AutocompleteDropdown,
      Dropdown,
      ActionBar,
      ImageInput,
      DsTextarea,
      MapView,
    },
    watch: {
      'mapData': {
        handler (newValue, oldValue) {
          if (newValue.length !== oldValue.length) {
            setTimeout(this.renderMap, 200)
          }
        },
      },
    },
    mounted () {
      if (!this.$store.getters.hasAccessToNewSpottingAreas) {
        this.$router.push('/')
      }

      this.fetchSpottingAreas(true)
      this.fetchPortfolioMembers()

      this.fetchPortfolios()
      this.fetchTrendMonitors()
      this.fetchDictionaryOptions()

      this.$bus.on('spottingAreaCreated', () => {
        this.fetchSpottingAreas(false, true)

        // Refresh the spotting areas list (crf. spotting areas overview page)
        this.$store.dispatch(SPOTTING_AREAS_ACTION_TYPES.FETCH_SPOTTING_AREAS_LIST)
      })

      this.$bus.on('spottingAreaUpdated', (context) => {
        this.fetchSpottingAreas(false)

        // Refresh the spotting areas list (crf. spotting areas overview page)
        this.$store.dispatch(SPOTTING_AREAS_ACTION_TYPES.FETCH_SPOTTING_AREAS_LIST)
      })

      this.$bus.on('spottingAreaDeleteConfirmation', (context) => {
        this.confirmDeleteSpottingArea(context.resource.id)
      })
    },
    mixins: [TranslationsMixin, UserMixin],
    beforeUnmount () {
      this.$bus.off('spottingAreaCreated')
    },
  })
</script>

<style lang="scss" scoped>
  @import "../../../scss/variables";

  :deep(.color-picker__color-items) {
    margin: 0 !important;
  }

  .color-picker__main-body {
    width: 150px !important;
    border: 0px !important;
    background-color: transparent !important;
  }

  .spotting-area-list-container {
    position: relative;

    .color-picker__color-header {
      border: 1px solid black;
      width: fit-content !important;
      padding: 0;


      span {
        margin-left: 0 !important;
      }
    }
  }

  .ecosystem-member-spotting-areas {
    .side-panel__spotting-area-header-button-container {
      display: flex;
    }

    .spotting-area__description {
      min-height: 75px;
      margin-bottom: 20px;
    }

    .spotting-area-list-container {
      display: flex;
      align-items: center;
      margin-right: 10px;

      &:hover {
        cursor: pointer;
      }

      .icon-button {
        margin: 0px 10px;
        color: white;
      }

      .multiselect.multiselect--datascouts .multiselect__tags {
        border: 0;
        color: var(--primary) !important;

        span {
          color: var(--primary) !important;
        }
      }
    }
  }
</style>
